import { useLayoutEffect, type DependencyList, type RefObject } from 'react';
import {
  currentLocalTimeMinutes,
  scheduleScrollTopForAnchor,
  scheduleScrollTopToCenterAnchor,
} from '@/utils/scheduleScroll';

export interface UseScheduleScrollToNowOptions {
  scrollRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  earliestTime: number;
  latestTime: number;
  slotMinutes?: number;
  pxPerSlot?: number;
  /** When false and enabled is false, scroll to fallback anchor instead of now. */
  fallbackAnchorMinutes?: number | null;
  deps?: DependencyList;
}

export function useScheduleScrollToNow({
  scrollRef,
  enabled,
  earliestTime,
  latestTime,
  slotMinutes = 30,
  pxPerSlot = 50,
  fallbackAnchorMinutes = null,
  deps = [],
}: UseScheduleScrollToNowOptions): void {
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const anchorMinutes = enabled
      ? currentLocalTimeMinutes()
      : fallbackAnchorMinutes;
    if (anchorMinutes == null) return;

    const scrollTop = enabled
      ? scheduleScrollTopToCenterAnchor(el, anchorMinutes, earliestTime, latestTime, {
          slotMinutes,
          pxPerSlot,
        })
      : scheduleScrollTopForAnchor(el, anchorMinutes, earliestTime, latestTime, {
          slotMinutes,
          pxPerSlot,
          paddingMinutesAbove: 60,
        });

    el.scrollTop = scrollTop;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller controls re-scroll via deps
  }, [scrollRef, enabled, earliestTime, latestTime, slotMinutes, pxPerSlot, fallbackAnchorMinutes, ...deps]);
}
